<template>
  <v-dialog
    v-if="currentEditWorkspace"
    v-model="isDialogOpen"
    width="637"
    content-class="v-dialog__form">
    <v-card>
      <v-card-title>
        <div class="d-flex align-center">
          Manage Workspace
        </div>
        <v-icon
          color="#fff"
          @click="isDialogOpen = false">
          mdi-close
        </v-icon>
      </v-card-title>
      <v-card-text>
        <div style="white-space: pre-line;">
          <strong>Note:</strong>
          {{ "\nSwitching will also modify the subscription in" }}
          <strong>Stripe.</strong>
          {{ "\nIf there is " }}
          <strong>no Subscription</strong>
          {{ "in Stripe, only the workspace data changes." }}
        </div>
        <v-radio-group v-model="radioGroup">
          <v-radio
            v-for="n in radioItems"
            :key="n.value"
            :label="n.name"
            :value="n.value"
            @change="workspaceTypeChanged(n.value)" />
        </v-radio-group>
        <div style="white-space: pre-line;">
          <strong>Note:</strong>
          {{ "\nIf this is on, guest/user limits below are" }}
          <strong>ignored</strong>
          {{ " in the app." }}
        </div>
        <app-switcher
          class="mt-0 mb-3"
          label-text="Unlimited Users"
          :value="currentUnlimitedUsers"
          @switchFlag="currentUnlimitedUsers = $event" />
        <div class="d-flex items-center align-center">
          <span class="mr-2">Guest Limit:</span>
          <div
            class="input-wrapper">
            <v-text-field
              v-model="guestLicensesInputValue"
              type="number" />
          </div>
          <span class="ml-4">
            [ Current: {{ currentEditWorkspace.guestLicenseLimit }} ]
          </span>
        </div>
        <div class="d-flex items-center align-center">
          <span class="mr-2">Users Limit:</span>
          <div
            class="input-wrapper">
            <v-text-field
              v-model="workspaceLicensesInputValue"
              type="number" />
          </div>
          <span class="ml-4">
            [ Current: {{
              currentEditWorkspace.type === TYPE_WORKSPACE_FREE
                ? currentEditWorkspace.freeLicenses
                : currentEditWorkspace.premiumLicenses
            }} ]
          </span>
        </div>
        <app-switcher
          v-if="useSourceryPages"
          :label-text="ALLOW_PAGES_LABEL_TEXT"
          :value="currentAllowPages"
          @switchFlag="currentAllowPages = $event" />
        <app-switcher
          :label-text="VERIFY_PAGE_LABEL_TEXT"
          :value="currentVerifyPage"
          @switchFlag="currentVerifyPage = $event" />
        <v-row
          class="mt-10"
          no-gutters
          justify="end">
          <v-btn
            height="64px"
            width="114px"
            color="lightBlue"
            @click="onUpdate">
            Update
          </v-btn>
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import { mapGetters } from 'vuex';
import {
  TYPE_WORKSPACE_FREE,
  TYPE_WORKSPACE_PREMIUM,
  TYPE_WORKSPACE_STARTER,
  TYPE_WORKSPACE_SELF_SERVE,
  TYPE_WORKSPACE_MANAGED,
  PAID_WORKSPACE_TYPES,
  WSTYPE_TO_DEFAULT_LICENSE_COUNTS,
} from '@/constants';
const ALLOW_PAGES_LABEL_TEXT = 'Workspace Pages functionality enabled';
const VERIFY_PAGE_LABEL_TEXT = 'Verified Manufacturer';
export default {
  name: 'WorkspaceDialog',
  components: {
    appSwitcher: () => import('../../App/AppSwitcher'),
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    currentEditWorkspace: {
      type: Object,
      default: null,
    },
  },
  data: () => ({
    VERIFY_PAGE_LABEL_TEXT,
    ALLOW_PAGES_LABEL_TEXT,
    radioItems: [
      {
        name: 'Free',
        value: TYPE_WORKSPACE_FREE,
      },
      {
        name: 'Starter',
        value: TYPE_WORKSPACE_STARTER,
      },
      {
        name: 'Self-serve',
        value: TYPE_WORKSPACE_SELF_SERVE,
      },
      {
        name: 'Managed',
        value: TYPE_WORKSPACE_MANAGED,
      },
    ],
    radioGroup: TYPE_WORKSPACE_FREE,
    guestLicensesInputValue: 0,
    workspaceLicensesInputValue: 0,
    currentUnlimitedUsers: false,
    currentAllowPages: false,
    currentVerifyPage: false,
  }),
  computed: {
    ...mapGetters({
      useSourceryPages: 'SiteConfiguration/useSourceryPagesCreationPublication',
    }),
    unlimitedUsers() {
      const { unlimitedUsers = false } = this.currentEditWorkspace ?? {
      };
      return !!unlimitedUsers;
    },
    verified() {
      const { verified = false } = this.currentEditWorkspace ?? {
      };
      return !!verified;
    },
    allowPages() {
      const { allowPages = false } = this.currentEditWorkspace ?? {
      };
      return !!allowPages;
    },
    isDialogOpen: {
      get() {
        return this.isOpen;
      },
      set(val) {
        this.$emit('setIsOpen', val);
      },
    },
    isCurrentWorkspacePaid() {
      return this.isTypePaid(this.currentEditWorkspace.type);
    },
    isSelectedTypePaid() {
      return this.isTypePaid(this.radioGroup);
    },
    minLicensesCountValue() {
      return this.currentEditWorkspace.usedLicenses;
    },
    errorMessage() {
      if (this.currentEditWorkspace.type === TYPE_WORKSPACE_FREE) {
        return `Value should be more than  ${this.minLicensesCountValue}`;
      } else {
        return `Please enter another value that is greater than or equal to the used licenses amount`;
      }
    },
  },
  watch: {
    isOpen(val) {
      if (val) {
        this.currentAllowPages = this.allowPages;
        this.currentVerifyPage = this.verified;
        this.currentUnlimitedUsers = this.unlimitedUsers;
      }
      this.radioGroup = this.currentEditWorkspace.type;
      this.guestLicensesInputValue = this.currentEditWorkspace.guestLicenseLimit;
      this.workspaceLicensesInputValue = this.currentEditWorkspace.type === 'free'
        ? this.currentEditWorkspace.freeLicenses
        : this.currentEditWorkspace.premiumLicenses;
    },
  },
  created() {
    this.TYPE_WORKSPACE_FREE = TYPE_WORKSPACE_FREE;
    this.TYPE_WORKSPACE_PREMIUM = TYPE_WORKSPACE_PREMIUM;
    this.TYPE_WORKSPACE_STARTER = TYPE_WORKSPACE_STARTER;
    this.TYPE_WORKSPACE_SELF_SERVE = TYPE_WORKSPACE_SELF_SERVE;
    this.TYPE_WORKSPACE_MANAGED = TYPE_WORKSPACE_MANAGED;
    this.PAID_WORKSAPCE_TYPES = PAID_WORKSPACE_TYPES;
    this.WSTYPE_TO_DEFAULT_LICENSE_COUNTS = WSTYPE_TO_DEFAULT_LICENSE_COUNTS;
  },
  methods: {
    onUpdate() {
      const freeLicenses = this.radioGroup === TYPE_WORKSPACE_FREE
        ? Number.parseInt(this.workspaceLicensesInputValue)
        : this.currentEditWorkspace.freeLicenses;
      const premiumLicenses = this.radioGroup !== TYPE_WORKSPACE_FREE
        ? Number.parseInt(this.workspaceLicensesInputValue)
        : this.currentEditWorkspace.premiumLicenses;
      this.$emit('onUpdate', {
        type: this.radioGroup,
        freeLicenses: freeLicenses,
        guestLicenseLimit: Number.parseInt(this.guestLicensesInputValue),
        premiumLicenses: premiumLicenses,
        allowPages: this.currentAllowPages,
        verified: this.currentVerifyPage,
        unlimitedUsers: this.currentUnlimitedUsers,
      });
    },
    isTypePaid(wsType) {
      return PAID_WORKSPACE_TYPES.includes(wsType);
    },
    workspaceTypeChanged(wsType) {
      const defaultGuestLicenseLimit = WSTYPE_TO_DEFAULT_LICENSE_COUNTS[wsType]?.guests ?? 0;
      const defaultUsersLicenseLimit = WSTYPE_TO_DEFAULT_LICENSE_COUNTS[wsType]?.users ?? 0;
      this.guestLicensesInputValue = Math.max(defaultGuestLicenseLimit);
      this.workspaceLicensesInputValue = Math.max(defaultUsersLicenseLimit);
    },
  },
};
</script>
<style lang="scss" scoped>
  .error-message{
    color:red;
    font-size: 12px;
  }
  .input-wrapper{
    width: 100px;
  }
  ::v-deep .v-btn--disabled{
    background: unset !important
  }
  .error-msg-wrapper{
    margin-top: 10px;
    height: 24px;
  }
  .input-wrapper ::v-deep .v-text-field {
    padding: 0;
    margin: 0;
  }
</style>
